import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Users,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinHousehold = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const response = await fetch(`${API_URL}/api/household/invite/${inviteId}`);
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Invalid invitation');
        }
        
        setInvite(data);
      } catch (err) {
        setError(err.message || 'Invalid invitation');
      } finally {
        setLoading(false);
      }
    };
    
    loadInvite();
  }, [inviteId]);

  const acceptInvite = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/auth?redirect=/join-household/${inviteId}`);
      return;
    }
    
    setJoining(true);
    try {
      const response = await api.post(`/household/join/${inviteId}`);
      toast.success(response.data.message);
      setJoined(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join household');
      setError(err.response?.data?.detail || 'Failed to join household');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>
                Go to Harvest & Hearth
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-primary/30">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Welcome to the Family! 🎉</h2>
            <p className="text-muted-foreground mb-6">
              You&apos;ve joined <strong>{invite?.household_name}</strong>. Redirecting to your dashboard...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-2">
            You&apos;re Invited! 👨‍👩‍👧‍👦
          </h2>
          
          <p className="text-muted-foreground mb-6">
            <strong>{invite?.invited_by_name}</strong> has invited you to join the{' '}
            <strong>&quot;{invite?.household_name}&quot;</strong> household on Harvest & Hearth.
          </p>
          
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 mb-6 text-left">
            <p className="text-sm font-medium mb-2">What you&apos;ll share:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Weekly meal plans
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Grocery lists
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Pantry items & expiration alerts
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Budget tracking
              </li>
            </ul>
          </div>
          
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
              {error}
            </div>
          )}
          
          <Button 
            size="lg" 
            className="w-full gap-2"
            onClick={acceptInvite}
            disabled={joining}
            data-testid="accept-invite-btn"
          >
            {joining ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Home className="h-5 w-5" />
            )}
            {isAuthenticated ? 'Join Household' : 'Sign In to Join'}
          </Button>
          
          {!isAuthenticated && (
            <p className="text-xs text-muted-foreground mt-4">
              Don&apos;t have an account?{' '}
              <Link to={`/auth?redirect=/join-household/${inviteId}`} className="text-primary hover:underline">
                Sign up free
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinHousehold;
